class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');

    if (this.mainDetailsToggle.classList.contains('mega-menu') && this.dataset.parentUrl) {
      this.setupMegaMenuDesktop();
    }
  }

  disconnectedCallback() {
    if (this.megaDesktopTeardown) this.megaDesktopTeardown();
  }

  setupMegaMenuDesktop() {
    this.megaParentUrl = this.dataset.parentUrl;
    this.desktopMql = window.matchMedia('(min-width: 990px)');
    this.megaEnterTimeout = null;
    this.megaLeaveTimeout = null;
    this.megaOpenDelayMs = 300;

    this.onMegaDesktopSummaryClick = (event) => {
      if (!this.desktopMql.matches) return;
      if (event.button !== 0) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (event.detail === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(this.megaParentUrl);
    };

    this.resetMegaMenuPanelAnimation = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.content.style.animation = 'none';
      void this.content.offsetHeight;
      this.content.style.removeProperty('animation');
    };

    this.openMegaMenuPanel = () => {
      if (this.mainDetailsToggle.hasAttribute('open')) return;

      this.closeOtherOpenMegaMenus();
      this.resetMegaMenuPanelAnimation();
      this.mainDetailsToggle.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', true);
    };

    this.onMegaDesktopEnter = () => {
      if (!this.desktopMql.matches) return;
      this.clearMegaLeaveTimeout();
      this.clearMegaEnterTimeout();

      if (this.mainDetailsToggle.hasAttribute('open')) return;

      this.megaEnterTimeout = window.setTimeout(() => {
        this.openMegaMenuPanel();
        this.megaEnterTimeout = null;
      }, this.megaOpenDelayMs);
    };

    this.onMegaDesktopLeave = () => {
      if (!this.desktopMql.matches) return;
      this.clearMegaEnterTimeout();
      this.clearMegaLeaveTimeout();
      this.megaLeaveTimeout = window.setTimeout(() => {
        this.close();
        this.megaLeaveTimeout = null;
      }, 120);
    };

    this.onMegaDesktopMqChange = () => {
      if (!this.desktopMql.matches) {
        this.clearMegaEnterTimeout();
        this.clearMegaLeaveTimeout();
        this.close();
      }
    };

    this.onMegaDesktopFocusIn = (event) => {
      if (!this.desktopMql.matches) return;
      if (this.mainDetailsToggle.hasAttribute('open')) return;

      const fromOutside = !event.relatedTarget || !this.contains(event.relatedTarget);
      if (!fromOutside) return;

      this.clearMegaEnterTimeout();
      this.openMegaMenuPanel();
    };

    this.onMegaDesktopContentPointerDown = () => {
      if (!this.desktopMql.matches) return;
      this.clearMegaLeaveTimeout();
      this.clearMegaEnterTimeout();
    };

    this.summary.addEventListener('click', this.onMegaDesktopSummaryClick, true);
    this.content.addEventListener('mousedown', this.onMegaDesktopContentPointerDown);
    this.addEventListener('mouseenter', this.onMegaDesktopEnter);
    this.addEventListener('mouseleave', this.onMegaDesktopLeave);
    this.addEventListener('focusin', this.onMegaDesktopFocusIn);

    if (this.desktopMql.addEventListener) {
      this.desktopMql.addEventListener('change', this.onMegaDesktopMqChange);
    } else {
      this.desktopMql.addListener(this.onMegaDesktopMqChange);
    }

    this.megaDesktopTeardown = () => {
      this.summary.removeEventListener('click', this.onMegaDesktopSummaryClick, true);
      this.content.removeEventListener('mousedown', this.onMegaDesktopContentPointerDown);
      this.removeEventListener('mouseenter', this.onMegaDesktopEnter);
      this.removeEventListener('mouseleave', this.onMegaDesktopLeave);
      this.removeEventListener('focusin', this.onMegaDesktopFocusIn);
      if (this.content?.style) this.content.style.removeProperty('animation');
      if (this.desktopMql.removeEventListener) {
        this.desktopMql.removeEventListener('change', this.onMegaDesktopMqChange);
      } else if (this.desktopMql.removeListener) {
        this.desktopMql.removeListener(this.onMegaDesktopMqChange);
      }
      this.clearMegaEnterTimeout();
      this.clearMegaLeaveTimeout();
      this.megaDesktopTeardown = null;
    };
  }

  clearMegaEnterTimeout() {
    if (this.megaEnterTimeout != null) {
      clearTimeout(this.megaEnterTimeout);
      this.megaEnterTimeout = null;
    }
  }

  clearMegaLeaveTimeout() {
    if (this.megaLeaveTimeout != null) {
      clearTimeout(this.megaLeaveTimeout);
      this.megaLeaveTimeout = null;
    }
  }

  closeOtherOpenMegaMenus() {
    document.querySelectorAll('header-menu details.mega-menu[open]').forEach((detailsEl) => {
      if (detailsEl === this.mainDetailsToggle) return;
      detailsEl.closest('header-menu')?.close?.();
    });
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
